import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { tutorApi } from '../services/api';
import { saveMonthYear, loadMonthYear } from '../utils/monthYearPersistence';
import { isNonSGTBrowser, formatDateTimeSGTLabelled, dateTimeToSGTIso } from '../utils/dateUtils';
import InvoicePreview from './InvoicePreview';
import MonthYearNavigator from './MonthYearNavigator';
import '../assets/MonthlySummaryEndorsement.css?v=2';
import '../assets/InvoiceManagement.css';

const MonthlySummary = ({ initialMonth, initialYear, onBack }) => {
  // Support URL params (/lessons/:year/:month) as well as props
  const params = useParams();
  const urlYear = params.year ? parseInt(params.year) : null;
  const urlMonth = params.month ? parseInt(params.month) : null;

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Date selection state - URL params take priority, then props, then localStorage
  const savedMonthYear = loadMonthYear('monthly_summary');
  const [selectedMonth, setSelectedMonth] = useState(urlMonth || initialMonth || savedMonthYear.month);
  const [selectedYear, setSelectedYear] = useState(urlYear || initialYear || savedMonthYear.year);
  const [endorsementNotes, setEndorsementNotes] = useState('');
  const [endorsing, setEndorsing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [disputingLesson, setDisputingLesson] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [submittingDispute, setSubmittingDispute] = useState(false);
  const [showCalendarView, setShowCalendarView] = useState(false);
  const [showAddLessonForm, setShowAddLessonForm] = useState(false);
  const [tutees, setTutees] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [completenessConfirmed, setCompletenessConfirmed] = useState(false);
  
  // Invoice viewing state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Filtering and sorting state
  const [selectedTutees, setSelectedTutees] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedModes, setSelectedModes] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  
  // Add lesson form state
  const [newLesson, setNewLesson] = useState({
    tuteeId: '',
    subject: '',
    date: '',
    time: '',
    duration: 1,
    mode: '',
    summary: ''
  });

  useEffect(() => {
    // Get current user data from localStorage
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
    
    fetchMonthlySummary();
    fetchTutees();
  }, [selectedMonth, selectedYear]);

  const fetchTutees = async () => {
    try {
      const response = await tutorApi.getTutees();
      setTutees(response.data || []);
    } catch (err) {
      console.error('Error fetching tutees:', err);
    }
  };

  const handleTuteeChange = (tuteeId) => {
    setNewLesson({ ...newLesson, tuteeId, subject: '', mode: '' });
    
    // Find selected tutee and get their subjects
    const selectedTutee = tutees.find(t => t._id === tuteeId);
    if (selectedTutee) {
      // Get subjects where this tutor is assigned
      const user = JSON.parse(localStorage.getItem('user'));
      const tutorId = user._id;
      
      const tutorSubjects = selectedTutee.subjects.filter(subject => 
        subject.isActive && subject.assignedTutors?.some(at => {
          const tid = typeof at === 'object' ? (at.tutor?._id || at.tutor) : at;
          return tid === tutorId;
        })
      );
      
      setAvailableSubjects(tutorSubjects);
    } else {
      setAvailableSubjects([]);
    }
  };

  const handleSubjectChange = (subjectName) => {
    const selectedSubject = availableSubjects.find(s => s.name === subjectName);
    
    if (selectedSubject) {
      setNewLesson({ 
        ...newLesson, 
        subject: subjectName,
        mode: selectedSubject.mode || ''
      });
    } else {
      setNewLesson({ 
        ...newLesson, 
        subject: subjectName,
        mode: ''
      });
    }
  };

  const fetchMonthlySummary = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await tutorApi.getMonthlySummary(selectedYear, selectedMonth);
      setSummary(response.data.data);
    } catch (err) {
      setError('Failed to fetch monthly summary');
      console.error('Error fetching monthly summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoice = async () => {
    try {
      setLoadingInvoice(true);
      const response = await tutorApi.getMyInvoice(selectedMonth, selectedYear);
      
      if (response.data.success) {
        setInvoiceData(response.data.data);
        setShowInvoiceModal(true);
      } else {
        alert('No invoice found for this period. The invoice may not have been generated yet.');
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      
      let errorMessage = 'Failed to fetch invoice';
      if (err.response?.status === 404) {
        errorMessage = 'Invoice not found. The invoice may not have been generated yet.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to view this invoice.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      alert(errorMessage);
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleMonthChange = (e) => {
    const newMonth = parseInt(e.target.value);
    setSelectedMonth(newMonth);
    saveMonthYear('monthly_summary', newMonth, selectedYear);
  };

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value);
    setSelectedYear(newYear);
    saveMonthYear('monthly_summary', selectedMonth, newYear);
  };

  const handleEndorseClick = () => {
    if (!completenessConfirmed) {
      alert('Please confirm that you have submitted all lessons for this month before endorsing.');
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmEndorse = async () => {
    try {
      setEndorsing(true);
      const response = await tutorApi.endorseMonthlySummary(
        selectedYear,
        selectedMonth,
        endorsementNotes
      );
      setSummary(response.data.data);
      setShowConfirmDialog(false);
      setEndorsementNotes('');
      alert('Monthly summary endorsed successfully!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to endorse summary');
      console.error('Error endorsing summary:', err);
    } finally {
      setEndorsing(false);
    }
  };

  const handleCancelEndorse = () => {
    setShowConfirmDialog(false);
  };

  const handleDisputeClick = (lesson) => {
    setDisputingLesson(lesson);
    setDisputeReason('');
  };

  const handleCancelDispute = () => {
    setDisputingLesson(null);
    setDisputeReason('');
  };

  const handleSubmitDispute = async () => {
    if (!disputeReason.trim()) {
      alert('Please provide a reason for the dispute');
      return;
    }

    if (disputeReason.length > 500) {
      alert('Dispute reason cannot exceed 500 characters');
      return;
    }

    try {
      setSubmittingDispute(true);
      await tutorApi.disputeLesson(disputingLesson._id, disputeReason);
      
      // Refresh the summary to show updated status
      await fetchMonthlySummary();
      
      setDisputingLesson(null);
      setDisputeReason('');
      alert('Lesson disputed successfully. An administrator will review your dispute.');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to dispute lesson');
      console.error('Error disputing lesson:', err);
    } finally {
      setSubmittingDispute(false);
    }
  };

  const handleAddLessonClick = () => {
    // Default date = the 1st of the month/year being viewed, built as a plain
    // SGT YYYY-MM-DD string (no local Date -> UTC round-trip, which would
    // shift the day backwards on a browser east of UTC such as SGT itself).
    const defaultDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
    // Default time = the current time in SGT (HH:MM 24h).
    const defaultTime = new Date().toLocaleTimeString('en-GB', {
      timeZone: 'Asia/Singapore',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    setNewLesson({
      tuteeId: '',
      subject: '',
      date: defaultDate,
      time: defaultTime,
      duration: 1,
      mode: '',
      summary: ''
    });
    setAvailableSubjects([]);
    setShowAddLessonForm(true);
  };

  const handleCancelAddLesson = () => {
    setShowAddLessonForm(false);
    setNewLesson({
      tuteeId: '',
      subject: '',
      date: '',
      time: '',
      duration: 1,
      mode: '',
      summary: ''
    });
    setAvailableSubjects([]);
  };

  const handleSubmitNewLesson = async () => {
    if (!newLesson.tuteeId || !newLesson.subject || !newLesson.date || !newLesson.time || !newLesson.mode) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      // Get tutor ID from logged-in user
      const user = JSON.parse(localStorage.getItem('user'));
      const tutorId = user._id;

      // Find the selected tutee and subject to get the rate
      const selectedTutee = tutees.find(t => t._id === newLesson.tuteeId);
      if (!selectedTutee) {
        alert('Selected tutee not found');
        return;
      }

      const selectedSubject = selectedTutee.subjects.find(s => s.name === newLesson.subject);
      if (!selectedSubject) {
        alert('Selected subject not found for this tutee');
        return;
      }

      // Interpret the picked date (YYYY-MM-DD) and time (HH:MM) as Singapore
      // time and convert to a UTC ISO string. dateTimeToSGTIso avoids the
      // local-vs-UTC off-by-one where a non-SGT browser shifted the day.
      const dateIso = dateTimeToSGTIso(newLesson.date, newLesson.time);

      // Capitalize mode to match enum values (Physical, Online)
      const capitalizedMode = newLesson.mode.charAt(0).toUpperCase() + newLesson.mode.slice(1).toLowerCase();
      
      const lessonData = {
        tutee: newLesson.tuteeId,
        tutor: tutorId,
        subject: newLesson.subject,
        date: dateIso,
        duration: newLesson.duration * 60, // Convert hours to minutes
        mode: capitalizedMode,
        summary: newLesson.summary || '', // Summary is optional
        rate: selectedSubject.rate // Tutee rate from subject
      };

      await tutorApi.submitLesson(lessonData);

      alert('Lesson added successfully!');
      setShowAddLessonForm(false);
      setCompletenessConfirmed(false); // Reset confirmation since lessons changed
      await fetchMonthlySummary(); // Refresh the summary
    } catch (err) {
      console.error('Error adding lesson:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to add lesson';
      alert(errorMessage);
    }
  };

  const toggleCalendarView = () => {
    setShowCalendarView(!showCalendarView);
  };

  const getDaysInMonth = () => {
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const days = [];
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  // Get unique tutees and subjects from lessons for filter options
  const getUniqueTutees = () => {
    if (!summary || !summary.lessons) return [];
    const tuteeMap = new Map();
    summary.lessons.forEach(lesson => {
      if (lesson.tutee && lesson.tutee._id) {
        tuteeMap.set(lesson.tutee._id, lesson.tutee.name);
      }
    });
    return Array.from(tuteeMap, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  };

  const getUniqueSubjects = () => {
    if (!summary || !summary.lessons) return [];
    const subjects = new Set(summary.lessons.map(lesson => lesson.subject));
    return Array.from(subjects).sort();
  };

  const getUniqueModes = () => {
    if (!summary || !summary.lessons) return [];
    const modes = new Set(summary.lessons.map(lesson => lesson.mode || 'Online'));
    return Array.from(modes).sort();
  };

  // Filter and sort lessons
  const getFilteredAndSortedLessons = () => {
    if (!summary || !summary.lessons) return [];
    
    let filtered = [...summary.lessons];
    
    // Apply tutee filter
    if (selectedTutees.length > 0) {
      filtered = filtered.filter(lesson => 
        lesson.tutee && selectedTutees.includes(lesson.tutee._id)
      );
    }
    
    // Apply subject filter
    if (selectedSubjects.length > 0) {
      filtered = filtered.filter(lesson => 
        selectedSubjects.includes(lesson.subject)
      );
    }
    
    // Apply mode filter
    if (selectedModes.length > 0) {
      filtered = filtered.filter(lesson => 
        selectedModes.includes(lesson.mode || 'Online')
      );
    }
    
    // Always sort by tutee, then subject, then date/time
    filtered.sort((a, b) => {
      // First, sort by tutee name
      const tuteeA = (a.tutee?.name || '').toLowerCase();
      const tuteeB = (b.tutee?.name || '').toLowerCase();
      if (tuteeA < tuteeB) return -1;
      if (tuteeA > tuteeB) return 1;
      
      // If tutee is the same, sort by subject
      const subjectA = (a.subject || '').toLowerCase();
      const subjectB = (b.subject || '').toLowerCase();
      if (subjectA < subjectB) return -1;
      if (subjectA > subjectB) return 1;
      
      // If subject is the same, sort by date/time
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateA - dateB;
    });
    
    return filtered;
  };

  // Handle sort column click
  // Toggle tutee selection
  const toggleTuteeFilter = (tuteeId) => {
    setSelectedTutees(prev => 
      prev.includes(tuteeId) 
        ? prev.filter(id => id !== tuteeId)
        : [...prev, tuteeId]
    );
  };

  // Toggle subject selection
  const toggleSubjectFilter = (subject) => {
    setSelectedSubjects(prev => 
      prev.includes(subject)
        ? prev.filter(s => s !== subject)
        : [...prev, subject]
    );
  };

  // Toggle mode selection
  const toggleModeFilter = (mode) => {
    setSelectedModes(prev => 
      prev.includes(mode)
        ? prev.filter(m => m !== mode)
        : [...prev, mode]
    );
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedTutees([]);
    setSelectedSubjects([]);
    setSelectedModes([]);
  };

  // Toggle dropdown open/close
  const toggleDropdown = (dropdownName) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.multi-select-dropdown')) {
        setOpenDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInMonth_old = () => {
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const days = [];
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getLessonDates = () => {
    if (!summary || !summary.lessons) return new Set();
    return new Set(
      summary.lessons.map(lesson => 
        new Date(lesson.date).getDate()
      )
    );
  };

  const months = [
    { value: 1, name: 'January' },
    { value: 2, name: 'February' },
    { value: 3, name: 'March' },
    { value: 4, name: 'April' },
    { value: 5, name: 'May' },
    { value: 6, name: 'June' },
    { value: 7, name: 'July' },
    { value: 8, name: 'August' },
    { value: 9, name: 'September' },
    { value: 10, name: 'October' },
    { value: 11, name: 'November' },
    { value: 12, name: 'December' }
  ];

  const years = [];
  for (let i = 2020; i <= 2030; i++) {
    years.push(i);
  }

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'endorsed':
        return 'status-badge endorsed';
      case 'disputed':
        return 'status-badge disputed';
      default:
        return 'status-badge pending';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'endorsed':
        return 'Endorsed';
      case 'disputed':
        return 'Disputed';
      default:
        return 'Pending';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-SG', {
      timeZone: 'Asia/Singapore',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return formatDateTimeSGTLabelled(dateString);
  };

  const renderProfitShareSection = () => {
    // Show this section for ALL tutors (profit sharers and regular tutors)
    const hasProfitShare = !!summary.profitShare;
    const hasAdjustments = summary.adjustments && summary.adjustments.length > 0;
    const hasRecurringPayments = summary.recurringPayments && summary.recurringPayments.length > 0;
    
    // If no profit share, adjustments, or recurring payments, don't show the section
    if (!hasProfitShare && !hasAdjustments && !hasRecurringPayments) {
      return null;
    }

    return (
      <div className="profit-share-section">
        <h3>{hasProfitShare ? `Profit Share Earnings (${summary.profitShare.allocatedPercentage}% Co-Founder)` : 'Additional Payments'}</h3>

        {/* Commission-Generating Lessons Table - Only for profit sharers */}
        {hasProfitShare && (
          <>
            <div className="commission-lessons-table">
              <h4>Commission-Generating Lessons</h4>
              {(() => {
                const filteredCommissionLessons = summary.profitShare.commissionLessons?.filter(lesson => 
                  lesson.commission && Math.abs(lesson.commission) > 0
                ) || [];
                
                if (filteredCommissionLessons.length === 0) {
                  return <p className="no-data">No commission-generating lessons this month</p>;
                }
                
                return (
                  <>
                    <table>
                      <thead>
                        <tr>
                          <th className={`col-date-time${isNonSGTBrowser() ? ' sgt-tz-col-header' : ''}`}>Date & Time</th>
                          <th className="col-tutor">Tutor</th>
                          <th className="col-tutee">Tutee</th>
                          <th className="col-subject">Subject</th>
                          <th className="col-duration">Duration</th>
                          <th className="col-tutee-rate">Tutee Rate</th>
                          <th className="col-tutor-rate">Tutor Rate</th>
                          <th className="col-commission">Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCommissionLessons.map((lesson, index) => (
                          <tr key={index}>
                            <td className="col-date-time">{formatDateTime(lesson.date)}</td>
                            <td className="col-tutor">{lesson.tutor?.name || 'N/A'}</td>
                            <td className="col-tutee">{lesson.tutee?.name || 'N/A'}</td>
                            <td className="col-subject">{lesson.subject}</td>
                            <td className="col-duration">{(lesson.duration / 60).toFixed(1)} hrs</td>
                            <td className="col-tutee-rate">${lesson.tuteeRate.toFixed(2)}</td>
                            <td className="col-tutor-rate">${lesson.tutorRate.toFixed(2)}</td>
                            <td className="col-commission">${Math.abs(lesson.commission).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p style={{ textAlign: 'right', marginTop: '10px', fontSize: '14px', color: '#666' }}>
                      <strong>Total Commission Pool:</strong> ${Math.abs(summary.profitShare.totalCommissionPool).toFixed(2)}
                    </p>
                  </>
                );
              })()}
            </div>
          </>
        )}

        {/* Adjustments & Payments Table - Show for ALL tutors */}
        {(hasAdjustments || hasRecurringPayments) && (
          <div className="adjustments-payments-section">
            <h4>Adjustments & Payments</h4>
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Pay To</th>
                  <th>Amount</th>
                  <th>Added By</th>
                </tr>
              </thead>
              <tbody>
                {/* Show adjustments (includes profit share deductions) */}
                {hasAdjustments && summary.adjustments.map((adjustment, index) => {
                  // Extract recipient name from reason if it's a profit share deduction
                  let description = adjustment.reason || adjustment.description || 'Payment Adjustment';
                  let payTo = 'You';
                  let typeLabel = adjustment.type ? adjustment.type.toUpperCase() : (adjustment.amount >= 0 ? 'BONUS' : 'DEDUCTION');
                  
                  if (adjustment.isProfitShareDeduction) {
                    // Extract name from "Description (to Name)" format
                    const match = description.match(/^(.+?)\s*\(to\s+(.+?)\)$/);
                    if (match) {
                      description = match[1].trim();
                      payTo = match[2].trim();
                    }
                    
                    // For recurring payment deductions, show original amount in brackets
                    if (adjustment.type === 'recurring_payment' && adjustment.originalAmount) {
                      description = `${description} ($${adjustment.originalAmount.toFixed(2)})`;
                      typeLabel = 'RECURRING PAYMENT';
                    }
                  }
                  
                  return (
                    <tr key={`adj-${index}`}>
                      <td>{description}</td>
                      <td>
                        <span className={`type-badge ${adjustment.amount >= 0 ? 'bonus' : 'deduction'}`}>
                          {typeLabel}
                        </span>
                      </td>
                      <td>{payTo}</td>
                      <td className={adjustment.amount >= 0 ? 'positive' : 'negative'}>
                        {adjustment.amount >= 0 ? '+' : ''}${Math.abs(adjustment.amount).toFixed(2)}
                      </td>
                      <td>{adjustment.createdBy?.name || 'Admin'}</td>
                    </tr>
                  );
                })}
                
                {/* Show recurring payments */}
                {hasRecurringPayments && summary.recurringPayments.map((payment, index) => (
                  <tr key={`rp-${index}`}>
                    <td>{payment.reason || payment.description || 'Recurring Payment'}</td>
                    <td>
                      <span className="type-badge recurring">
                        RECURRING PAYMENT
                      </span>
                    </td>
                    <td>You</td>
                    <td className="positive">
                      +${payment.amount.toFixed(2)}
                    </td>
                    <td>{payment.createdBy?.name || 'Admin'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Profit Share Summary - Only for profit sharers */}
        {hasProfitShare && (
          <div className="profit-share-summary">
            <div className="summary-row">
              <span>Your Commission Allocation ({summary.profitShare.allocatedPercentage}%):</span>
              <span>${Math.abs(summary.profitShare.grossProfitShare).toFixed(2)}</span>
            </div>

            {(() => {
              // Sum up the amounts shown in the table above
              const adjustmentsTotal = hasAdjustments ? 
                summary.adjustments.reduce((sum, adj) => sum + adj.amount, 0) : 0;
              const recurringTotal = hasRecurringPayments ? 
                summary.recurringPayments.reduce((sum, rp) => sum + rp.amount, 0) : 0;
              const paymentsTotal = adjustmentsTotal + recurringTotal;
              
              if (paymentsTotal !== 0) {
                return (
                  <div className={`summary-row ${paymentsTotal >= 0 ? 'bonus' : 'deduction'}`}>
                    <span>Adjustments & Payments:</span>
                    <span>{paymentsTotal >= 0 ? '+' : ''}${Math.abs(paymentsTotal).toFixed(2)}</span>
                  </div>
                );
              }
              return null;
            })()}

            <div className="summary-row total">
              <span>Net Profit Share:</span>
              <span className={(() => {
                // Calculate net profit share from displayed values
                const adjustmentsTotal = hasAdjustments ? 
                  summary.adjustments.reduce((sum, adj) => sum + adj.amount, 0) : 0;
                const recurringTotal = hasRecurringPayments ? 
                  summary.recurringPayments.reduce((sum, rp) => sum + rp.amount, 0) : 0;
                const netProfitShare = summary.profitShare.grossProfitShare + adjustmentsTotal + recurringTotal;
                return netProfitShare < 0 ? 'negative' : '';
              })()}>
                ${(() => {
                  // Calculate net profit share from displayed values
                  const adjustmentsTotal = hasAdjustments ? 
                    summary.adjustments.reduce((sum, adj) => sum + adj.amount, 0) : 0;
                  const recurringTotal = hasRecurringPayments ? 
                    summary.recurringPayments.reduce((sum, rp) => sum + rp.amount, 0) : 0;
                  const netProfitShare = summary.profitShare.grossProfitShare + adjustmentsTotal + recurringTotal;
                  return netProfitShare.toFixed(2);
                })()}
              </span>
            </div>

            {(() => {
              // Calculate net profit share from displayed values
              const adjustmentsTotal = hasAdjustments ? 
                summary.adjustments.reduce((sum, adj) => sum + adj.amount, 0) : 0;
              const recurringTotal = hasRecurringPayments ? 
                summary.recurringPayments.reduce((sum, rp) => sum + rp.amount, 0) : 0;
              const netProfitShare = summary.profitShare.grossProfitShare + adjustmentsTotal + recurringTotal;
              
              if (netProfitShare < 0) {
                return (
                  <div className="warning-message">
                    ⚠️ Net profit share is negative due to deductions exceeding gross profit share
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="loading">Loading monthly summary...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!summary) return <div className="error">No summary data available</div>;

  const isEndorsed = summary.endorsementStatus === 'endorsed';
  const hasDisputes = summary.hasDisputes;
  const canEndorse = !isEndorsed && !hasDisputes;

  return (
    <div className="monthly-summary">
      <div className="summary-header">
        <div className="summary-header-left">
          {onBack && (
            <button className="btn-back-modern" onClick={onBack} aria-label="Back to overview">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12 4L6 10l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Overview
            </button>
          )}
          <h2>Monthly Lesson Summary</h2>
        </div>
        <MonthYearNavigator
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={(month) => {
            setSelectedMonth(month);
            saveMonthYear('monthly_summary', month, selectedYear);
          }}
          onYearChange={(year) => {
            setSelectedYear(year);
            saveMonthYear('monthly_summary', selectedMonth, year);
          }}
          showLabel={false}
        />
      </div>

      <div className="status-cards-grid">
        {/* Endorsement Status Card */}
        <div className="status-card endorsement-card">
          {isEndorsed && summary.endorsedAt ? (
            <>
              <div className="status-badge endorsed">Endorsed</div>
              <div className="card-date-time">
                <span className="card-date">
                  {new Date(summary.endorsedAt).toLocaleDateString('en-SG', {
                    timeZone: 'Asia/Singapore',
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
                <span className="card-time">
                  {new Date(summary.endorsedAt).toLocaleTimeString('en-SG', {
                    timeZone: 'Asia/Singapore',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </span>
              </div>
              <button 
                className="btn-view-invoice"
                onClick={fetchInvoice}
                disabled={loadingInvoice}
              >
                {loadingInvoice ? 'Loading...' : 'View Invoice'}
              </button>
            </>
          ) : (
            <div className={getStatusBadgeClass(summary.endorsementStatus)}>
              {getStatusText(summary.endorsementStatus)}
            </div>
          )}
        </div>

        {/* Payment Status Card */}
        {isEndorsed && (
          <div className="status-card payment-card">
            {summary.paymentStatus === 'paid' && summary.paidAt ? (
              <>
                <div className="status-badge paid">Paid</div>
                <div className="card-date-time">
                  <span className="card-date">
                    {new Date(summary.paidAt).toLocaleDateString('en-SG', {
                      timeZone: 'Asia/Singapore',
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                  <span className="card-time">
                    {new Date(summary.paidAt).toLocaleTimeString('en-SG', {
                      timeZone: 'Asia/Singapore',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </span>
                </div>
                {summary.paymentNotes && (
                  <div className="payment-notes-box">
                    <span className="notes-label">Notes:</span>
                    <p className="notes-text">{summary.paymentNotes}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="status-badge pending">Pending</div>
                <p className="pending-message">
                  Payment will be processed by admin team
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {hasDisputes && (
        <div className="dispute-warning">
          <strong>⚠️ Warning:</strong> This summary has disputed lessons. Please resolve all
          disputes before endorsing.
        </div>
      )}

      {summary.lessons.length === 0 ? (
        <div className="no-lessons">
          <p>
            No lessons found for {months.find(m => m.value === selectedMonth)?.name}{' '}
            {selectedYear}.
          </p>
        </div>
      ) : (
        <>
          {/* Lessons Section */}
          <div className="lessons-section">
            <div className="lessons-header">
              <h3>Lessons Conducted</h3>
              
              {/* Stat Cards */}
              <div className="lessons-stats-cards">
                <div className="lessons-stat-card">
                  <div className="stat-label">Lessons</div>
                  <div className="stat-value">{summary.totalLessons}</div>
                </div>
                <div className="lessons-stat-card">
                  <div className="stat-label">Hours</div>
                  <div className="stat-value">{summary.lessons.reduce((sum, lesson) => sum + (lesson.duration / 60), 0).toFixed(1)}</div>
                </div>
                <div className="lessons-stat-card">
                  <div className="stat-label">Net Earned</div>
                  <div className="stat-value">${summary.totalEarnings.toFixed(2)}</div>
                </div>
              </div>
            </div>

            {/* Filter Section */}
            <div className="lessons-filters">
              <div style={{ display: 'flex', flexDirection: 'row', gap: '15px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {/* Tutee Filter */}
                <div style={{ flex: '1', minWidth: '180px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>Tutee:</label>
                  <div className="multi-select-dropdown">
                    <div 
                      className="selected-display"
                      onClick={() => toggleDropdown('tutee')}
                    >
                      {selectedTutees.length === 0 ? (
                        <span className="placeholder">All Tutees</span>
                      ) : (
                        <span className="selected-count">{selectedTutees.length} selected</span>
                      )}
                      <span className="dropdown-arrow">{openDropdown === 'tutee' ? '▲' : '▼'}</span>
                    </div>
                    {openDropdown === 'tutee' && (
                      <div className="dropdown-options">
                        {getUniqueTutees().map(tutee => (
                          <label key={tutee.id} className="dropdown-option">
                            <input
                              type="checkbox"
                              checked={selectedTutees.includes(tutee.id)}
                              onChange={() => toggleTuteeFilter(tutee.id)}
                            />
                            <span>{tutee.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Subject Filter */}
                <div style={{ flex: '1', minWidth: '180px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>Subject:</label>
                  <div className="multi-select-dropdown">
                    <div 
                      className="selected-display"
                      onClick={() => toggleDropdown('subject')}
                    >
                      {selectedSubjects.length === 0 ? (
                        <span className="placeholder">All Subjects</span>
                      ) : (
                        <span className="selected-count">{selectedSubjects.length} selected</span>
                      )}
                      <span className="dropdown-arrow">{openDropdown === 'subject' ? '▲' : '▼'}</span>
                    </div>
                    {openDropdown === 'subject' && (
                      <div className="dropdown-options">
                        {getUniqueSubjects().map(subject => (
                          <label key={subject} className="dropdown-option">
                            <input
                              type="checkbox"
                              checked={selectedSubjects.includes(subject)}
                              onChange={() => toggleSubjectFilter(subject)}
                            />
                            <span>{subject}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Mode Filter */}
                <div style={{ flex: '1', minWidth: '180px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>Mode:</label>
                  <div className="multi-select-dropdown">
                    <div 
                      className="selected-display"
                      onClick={() => toggleDropdown('mode')}
                    >
                      {selectedModes.length === 0 ? (
                        <span className="placeholder">All Modes</span>
                      ) : (
                        <span className="selected-count">{selectedModes.length} selected</span>
                      )}
                      <span className="dropdown-arrow">{openDropdown === 'mode' ? '▲' : '▼'}</span>
                    </div>
                    {openDropdown === 'mode' && (
                      <div className="dropdown-options">
                        {getUniqueModes().map(mode => (
                          <label key={mode} className="dropdown-option">
                            <input
                              type="checkbox"
                              checked={selectedModes.includes(mode)}
                              onChange={() => toggleModeFilter(mode)}
                            />
                            <span>{mode}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Clear Filters Button */}
                {(selectedTutees.length > 0 || selectedSubjects.length > 0 || selectedModes.length > 0) && (
                  <button className="btn-clear-filters" onClick={clearFilters}>
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            <div className="lessons-table-container">
            <table className="lessons-table">
              <thead>
                <tr>
                  <th className={`col-date-time${isNonSGTBrowser() ? ' sgt-tz-col-header' : ''}`}>Date & Time</th>
                  <th className="col-tutee">Tutee</th>
                  <th className="col-subject">Subject</th>
                  <th className="col-mode">Mode</th>
                  <th className="col-duration">Duration (hrs)</th>
                  <th className="col-rate">Tutor Rate</th>
                  <th className="col-summary">Summary</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredAndSortedLessons().map((lesson) => (
                  <tr
                    key={lesson._id}
                    className={`${lesson.isDisputed && !lesson.resolvedAt ? 'disputed-lesson' : ''} ${lesson.resolvedAt ? 'resolved-lesson' : ''}`}
                  >
                    <td className="col-date-time">{formatDateTime(lesson.date)}</td>
                    <td className="col-tutee">{lesson.tutee?.name || 'N/A'}</td>
                    <td className="col-subject">{lesson.subject}</td>
                    <td className="col-mode">{lesson.mode || 'Online'}</td>
                    <td className="col-duration">{(lesson.duration / 60).toFixed(1)}</td>
                    <td className="col-rate">${(lesson.tutorRate || 0).toFixed(2)}</td>
                    <td className="col-summary">{lesson.summary}</td>
                    <td className="col-actions">
                      {lesson.isDisputed && !lesson.resolvedAt ? (
                        <span className="disputed-badge" title={`Disputed: ${lesson.disputeReason || 'No reason provided'}`}>
                          ⚠️ Disputed
                        </span>
                      ) : lesson.resolvedAt ? (
                        <span 
                          className="resolved-badge" 
                          title={`Resolved on ${new Date(lesson.resolvedAt).toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore' })}\n\n${lesson.disputeResolution || 'No resolution notes provided'}`}
                        >
                          ✓ Resolved
                        </span>
                      ) : !isEndorsed ? (
                        <button
                          className="dispute-btn"
                          onClick={() => handleDisputeClick(lesson)}
                        >
                          Dispute
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </>
      )}

      {renderProfitShareSection()}

      {/* Final Summary Section */}
      <div className="final-summary-section">
        <h3>Payment Summary</h3>
        <div className="final-summary-box">
          <div className="summary-line">
            <span>Lesson Earnings:</span>
            <span className="amount">${summary.totalEarnings.toFixed(2)}</span>
          </div>
          
          {summary.profitShare && (
            <div className="summary-line">
              <span>Net Profit Share ({summary.profitShare.allocatedPercentage}%):</span>
              <span className={`amount ${(() => {
                // Calculate net profit share from displayed values
                const hasAdjustments = summary.adjustments && summary.adjustments.length > 0;
                const hasRecurringPayments = summary.recurringPayments && summary.recurringPayments.length > 0;
                const adjustmentsTotal = hasAdjustments ? 
                  summary.adjustments.reduce((sum, adj) => sum + adj.amount, 0) : 0;
                const recurringTotal = hasRecurringPayments ? 
                  summary.recurringPayments.reduce((sum, rp) => sum + rp.amount, 0) : 0;
                const netProfitShare = summary.profitShare.grossProfitShare + adjustmentsTotal + recurringTotal;
                return netProfitShare >= 0 ? 'positive' : 'negative';
              })()}`}>
                {(() => {
                  // Calculate net profit share from displayed values
                  const hasAdjustments = summary.adjustments && summary.adjustments.length > 0;
                  const hasRecurringPayments = summary.recurringPayments && summary.recurringPayments.length > 0;
                  const adjustmentsTotal = hasAdjustments ? 
                    summary.adjustments.reduce((sum, adj) => sum + adj.amount, 0) : 0;
                  const recurringTotal = hasRecurringPayments ? 
                    summary.recurringPayments.reduce((sum, rp) => sum + rp.amount, 0) : 0;
                  const netProfitShare = summary.profitShare.grossProfitShare + adjustmentsTotal + recurringTotal;
                  return (netProfitShare >= 0 ? '+' : '') + '$' + netProfitShare.toFixed(2);
                })()}
              </span>
            </div>
          )}
          
          <div className="summary-divider"></div>
          
          <div className="summary-line net-earnings">
            <span>NET EARNINGS:</span>
            <span className="amount">
              ${(() => {
                let total = summary.totalEarnings;
                
                // Add net profit share if exists (calculate from displayed values)
                if (summary.profitShare) {
                  const hasAdjustments = summary.adjustments && summary.adjustments.length > 0;
                  const hasRecurringPayments = summary.recurringPayments && summary.recurringPayments.length > 0;
                  const adjustmentsTotal = hasAdjustments ? 
                    summary.adjustments.reduce((sum, adj) => sum + adj.amount, 0) : 0;
                  const recurringTotal = hasRecurringPayments ? 
                    summary.recurringPayments.reduce((sum, rp) => sum + rp.amount, 0) : 0;
                  const netProfitShare = summary.profitShare.grossProfitShare + adjustmentsTotal + recurringTotal;
                  total += netProfitShare;
                }
                
                // For non-profit sharers, add adjustments and recurring separately
                if (!summary.profitShare) {
                  // Add adjustments (positive amounts are additions, negative are deductions)
                  if (summary.adjustments && summary.adjustments.length > 0) {
                    const adjustmentsTotal = summary.adjustments.reduce((sum, adj) => sum + adj.amount, 0);
                    total += adjustmentsTotal;
                  }
                  
                  // Add recurring payments (positive amounts are additions, negative are deductions)
                  if (summary.recurringPayments && summary.recurringPayments.length > 0) {
                    const recurringTotal = summary.recurringPayments.reduce((sum, rp) => sum + rp.amount, 0);
                    total += recurringTotal;
                  }
                }
                
                return total.toFixed(2);
              })()}
            </span>
          </div>
        </div>
      </div>

      {!isEndorsed && (
        <>
          <div className="completeness-check-section">
            <h3>Before You Endorse</h3>
            <div className="action-buttons">
              <button className="btn-calendar" onClick={toggleCalendarView}>
                {showCalendarView ? 'Hide Calendar' : 'View Calendar'}
              </button>
              <button className="btn-add-lesson" onClick={handleAddLessonClick}>
                + Add Missing Lesson
              </button>
            </div>

            {showCalendarView && (
              <div className="calendar-view">
                <h4>Lesson Days for {months.find(m => m.value === selectedMonth)?.name} {selectedYear}</h4>
                <div className="calendar-grid">
                  {getDaysInMonth().map(day => {
                    const hasLesson = getLessonDates().has(day);
                    return (
                      <div 
                        key={day} 
                        className={`calendar-day ${hasLesson ? 'has-lesson' : ''}`}
                      >
                        {day}
                      </div>
                    );
                  })}
                </div>
                <div className="calendar-legend">
                  <span className="legend-item">
                    <span className="legend-color has-lesson"></span> Lesson submitted
                  </span>
                  <span className="legend-item">
                    <span className="legend-color no-lesson"></span> No lesson
                  </span>
                </div>
              </div>
            )}

            <div className="completeness-confirmation">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={completenessConfirmed}
                  onChange={(e) => setCompletenessConfirmed(e.target.checked)}
                />
                <span>
                  I confirm that I have submitted all lessons for this month and the information is accurate.
                </span>
              </label>
            </div>
          </div>

          <div className="endorsement-section">
            <div className="endorsement-notes">
              <label htmlFor="endorsementNotes">Optional Notes:</label>
              <textarea
                id="endorsementNotes"
                value={endorsementNotes}
                onChange={(e) => setEndorsementNotes(e.target.value)}
                placeholder="Add any notes or comments about this month's lessons..."
                rows="3"
              />
            </div>
            <button
              className="endorse-btn"
              onClick={handleEndorseClick}
              disabled={!canEndorse || endorsing || !completenessConfirmed}
            >
              {endorsing ? 'Endorsing...' : 'Endorse Summary'}
            </button>
            {!canEndorse && hasDisputes && (
              <p className="endorse-disabled-message">
                Cannot endorse while there are disputed lessons
              </p>
            )}
            {!completenessConfirmed && canEndorse && (
              <p className="endorse-disabled-message">
                Please confirm that all lessons have been submitted
              </p>
            )}
          </div>
        </>
      )}

      {showConfirmDialog && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Endorsement</h3>
            <p>
              Are you sure you want to endorse this monthly summary? This confirms that all
              lesson records are accurate.
            </p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={handleCancelEndorse}
                disabled={endorsing}
              >
                Cancel
              </button>
              <button
                className="btn-confirm"
                onClick={handleConfirmEndorse}
                disabled={endorsing}
              >
                {endorsing ? 'Endorsing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {disputingLesson && (
        <div className="modal-overlay" onClick={(e) => e.target.className === 'modal-overlay' && handleCancelDispute()}>
          <div className="dispute-modal">
            <div className="modal-header">
              <h3>Dispute Lesson</h3>
              <button className="close-btn" onClick={handleCancelDispute}>×</button>
            </div>
            
            <div className="modal-body">
              <p className="modal-description">
                Please review the lesson details and provide a reason for your dispute.
              </p>
              
              <div className="lesson-info-card">
                <div className="info-row">
                  <div className="info-col">
                    <span className="info-label">Date & Time</span>
                    <span className="info-value">{formatDateTime(disputingLesson.date)}</span>
                  </div>
                  <div className="info-col">
                    <span className="info-label">Tutee</span>
                    <span className="info-value">{disputingLesson.tutee?.name || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="info-row">
                  <div className="info-col full-width">
                    <span className="info-label">Subject</span>
                    <span className="info-value">{disputingLesson.subject}</span>
                  </div>
                </div>
                
                <div className="info-row">
                  <div className="info-col">
                    <span className="info-label">Mode</span>
                    <span className="info-value">{disputingLesson.mode || 'Online'}</span>
                  </div>
                  <div className="info-col">
                    <span className="info-label">Duration</span>
                    <span className="info-value">{(disputingLesson.duration / 60).toFixed(1)} hrs</span>
                  </div>
                </div>
                
                <div className="info-row">
                  <div className="info-col">
                    <span className="info-label">Tutor Rate</span>
                    <span className="info-value">${(disputingLesson.tutorRate || 0).toFixed(2)}/hr</span>
                  </div>
                  <div className="info-col">
                    <span className="info-label">Amount</span>
                    <span className="info-value amount-highlight">
                      ${((disputingLesson.tutorRate || 0) * (disputingLesson.duration / 60)).toFixed(2)}
                    </span>
                  </div>
                </div>
                
                {disputingLesson.summary && (
                  <div className="info-row">
                    <div className="info-col full-width">
                      <span className="info-label">Lesson Summary</span>
                      <div className="summary-text">{disputingLesson.summary}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="dispute-reason-section">
                <label htmlFor="disputeReason" className="form-label">
                  Reason for Dispute <span className="required">*</span>
                </label>
                <textarea
                  id="disputeReason"
                  className="dispute-textarea"
                  value={disputeReason}
                  onChange={(e) => setDisputeReason(e.target.value)}
                  placeholder="Please explain why you are disputing this lesson (e.g., incorrect date, duration, rate, or lesson did not occur)..."
                  rows="4"
                  maxLength="500"
                />
                <div className="character-count">
                  {disputeReason.length} / 500 characters
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleCancelDispute}
                disabled={submittingDispute}
              >
                Cancel
              </button>
              <button
                className="btn-submit"
                onClick={handleSubmitDispute}
                disabled={submittingDispute || !disputeReason.trim()}
              >
                {submittingDispute ? 'Submitting...' : 'Submit Dispute'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddLessonForm && (
        <div className="modal-overlay">
          <div className="modal-content add-lesson-modal">
            <h3>Add Missing Lesson</h3>
            <div className="form-group">
              <label htmlFor="tuteeSelect">
                Tutee: <span className="required">*</span>
              </label>
              <select
                id="tuteeSelect"
                value={newLesson.tuteeId}
                onChange={(e) => handleTuteeChange(e.target.value)}
                required
              >
                <option value="">Select a tutee</option>
                {tutees.map(tutee => (
                  <option key={tutee._id} value={tutee._id}>
                    {tutee.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="subjectInput">
                Subject: <span className="required">*</span>
              </label>
              <select
                id="subjectInput"
                value={newLesson.subject}
                onChange={(e) => handleSubjectChange(e.target.value)}
                required
                disabled={!newLesson.tuteeId}
              >
                <option value="">Select subject</option>
                {availableSubjects.map((subject, idx) => (
                  <option key={idx} value={subject.name}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dateInput">
                  Date: <span className="required">*</span>
                </label>
                <input
                  id="dateInput"
                  type="date"
                  value={newLesson.date}
                  onChange={(e) => setNewLesson({ ...newLesson, date: e.target.value })}
                  min={`${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`}
                  max={`${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${new Date(selectedYear, selectedMonth, 0).getDate()}`}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="timeInput">
                  Time: <span className="required">*</span>
                </label>
                <input
                  id="timeInput"
                  type="time"
                  value={newLesson.time}
                  onChange={(e) => setNewLesson({ ...newLesson, time: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="durationInput">
                  Duration (hours): <span className="required">*</span>
                </label>
                <input
                  id="durationInput"
                  type="number"
                  value={newLesson.duration}
                  onChange={(e) => setNewLesson({ ...newLesson, duration: parseFloat(e.target.value) })}
                  min="0.25"
                  step="0.25"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="modeInput">
                  Mode: <span className="required">*</span>
                </label>
                <select
                  id="modeInput"
                  value={newLesson.mode}
                  onChange={(e) => setNewLesson({ ...newLesson, mode: e.target.value })}
                  required
                  disabled={!newLesson.subject}
                >
                  <option value="">Select mode</option>
                  {newLesson.subject && (() => {
                    const selectedSubject = availableSubjects.find(s => s.name === newLesson.subject);
                    if (!selectedSubject) return null;
                    
                    const subjectMode = selectedSubject.mode?.toLowerCase();
                    
                    // If subject supports both modes, show both options
                    if (subjectMode === 'both') {
                      return (
                        <>
                          <option value="Online">Online</option>
                          <option value="Physical">Physical</option>
                        </>
                      );
                    }
                    
                    // Otherwise, only show the subject's mode
                    return (
                      <option value={selectedSubject.mode}>
                        {selectedSubject.mode?.charAt(0).toUpperCase() + selectedSubject.mode?.slice(1).toLowerCase()}
                      </option>
                    );
                  })()}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="summaryInput">Lesson Summary:</label>
              <textarea
                id="summaryInput"
                value={newLesson.summary}
                onChange={(e) => setNewLesson({ ...newLesson, summary: e.target.value })}
                placeholder="Brief description of what was covered..."
                rows="3"
              />
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={handleCancelAddLesson}>
                Cancel
              </button>
              <button className="btn-submit" onClick={handleSubmitNewLesson}>
                Add Lesson
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Preview Modal */}
      {showInvoiceModal && invoiceData && currentUser && (
        <div className="invoice-preview-overlay">
          <div className="invoice-preview-modal">
            <div className="invoice-preview-header">
              <h3>Invoice Preview</h3>
              <button 
                className="close-preview" 
                onClick={() => setShowInvoiceModal(false)}
                aria-label="Close invoice preview"
              >
                ×
              </button>
            </div>
            <div className="invoice-preview-content">
              <InvoicePreview
                invoiceType="tutor"
                recipient={currentUser}
                month={selectedMonth}
                year={selectedYear}
                invoiceData={invoiceData}
                onClose={() => setShowInvoiceModal(false)}
                isTutorView={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlySummary;
